import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Check, X } from "lucide-react";
import Pagination from "@/components/panel/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import { Badge, Button, DataTable, PageHeader } from "@/components/ui-kit";

/* Tom semântico do Badge no lugar das classes cruas de cor: pendente = atenção
   (alguém ainda precisa decidir), aprovado = o admin acatou o pedido,
   recusado = negado. */
const STATUS_TONE = {
  pendente: "warning",
  aprovado: "success",
  recusado: "danger",
};

const FILTERS = [["pendente", "Pendentes"], ["aprovado", "Aprovadas"], ["recusado", "Recusadas"], ["", "Todas"]];

export default function AdminChangeRequests() {
  const [filter, setFilter] = useState("pendente");

  const {
    items, total, page, lastPage, setPage, isLoading: loading, isError: error, refetch: load,
  } = usePaginatedList("/admin/change-requests", { status: filter || undefined });

  const decide = async (c, action) => {
    try {
      await api.post(`/admin/change-requests/${c.id}/${action}`);
      toast.success(action === "approve" ? "Solicitação aprovada" : "Solicitação recusada");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-change-requests-page">
      {/* A descrição não promete que aprovar altera o produto: o
          ChangeRequestController só aplica preço quando a solicitação traz
          payload.product_id + payload.new_price, e o formulário do vendedor
          manda apenas tipo/item/descrição. Dizer que o preço "já é gravado"
          faria o admin aprovar e não executar a mudança. */}
      <PageHeader
        eyebrow="Operação"
        title="Solicitações"
        icon={Inbox}
        description="Pedidos de preço, estoque e cadastro abertos pelo balcão e pelos vendedores. Aprovar registra a decisão para quem pediu acompanhar — a alteração em si você aplica em Produtos ou Estoque."
        filters={(
          /* Filtro único, com quatro rótulos curtos que cabem na linha mesmo em
             390px — por isso não vai para a folha de filtros do mobile: triar os
             pendentes é justamente o que a pessoa vem fazer aqui. */
          <div className="flex bg-white border border-slate-200 rounded-xl p-1 w-fit" role="group" aria-label="Filtrar por status">
            {FILTERS.map(([v, l]) => (
              <button key={v || "all"} type="button" onClick={() => setFilter(v)} data-testid={`cr-filter-${v || "all"}`}
                aria-pressed={filter === v}
                className={`text-xs font-semibold px-3 h-8 rounded-md transition-colors ${filter === v ? "bg-brand-600 text-white" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
        )}
      />

      <DataTable
        rowTestId="admin-cr-row"
        caption="Solicitações de alteração"
        rows={items}
        rowKey={(c) => c.id}
        loading={loading}
        error={error}
        onRetry={load}
        empty={{
          icon: Inbox,
          title: filter ? "Nenhuma solicitação nesse status" : "Nenhuma solicitação",
          desc: filter
            ? "Nada com esse filtro. Veja todas para conferir o que já foi decidido."
            : "As solicitações abertas pelo balcão e pelos vendedores aparecem aqui para aprovação.",
          // Não há como o admin criar uma solicitação: a única saída é abrir o filtro.
          action: filter ? <Button variant="secondary" onClick={() => setFilter("")}>Ver todas</Button> : null,
          testId: "admin-cr-empty",
        }}
        columns={[
          {
            key: "solicitante", header: "Solicitante",
            cell: (c) => (
              <div>
                <p className="font-medium text-slate-900">{c.requested_by_name}</p>
                <p className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString("pt-BR")}</p>
              </div>
            ),
          },
          {
            key: "tipo", header: "Tipo", className: "capitalize text-slate-600",
            cell: (c) => `${c.type}${c.entity ? ` · ${c.entity}` : ""}`,
          },
          {
            key: "descricao", header: "Descrição", className: "text-slate-600 text-xs max-w-xs",
            cell: (c) => c.note || "—",
          },
          {
            key: "status", header: "Status",
            cell: (c) => <Badge tone={STATUS_TONE[c.status]} className="capitalize">{c.status}</Badge>,
          },
          {
            key: "acoes", header: "Ações", align: "center", className: "whitespace-nowrap",
            cell: (c) => (c.status === "pendente" ? (
              <>
                <Button variant="ghost" size="iconSm" onClick={() => decide(c, "approve")} data-testid="admin-cr-approve"
                  title="Aprovar" aria-label={`Aprovar solicitação de ${c.requested_by_name}`}
                  className="text-emerald-600 hover:text-emerald-700"><Check size={17} /></Button>
                <Button variant="ghost" size="iconSm" onClick={() => decide(c, "reject")} data-testid="admin-cr-reject"
                  title="Recusar" aria-label={`Recusar solicitação de ${c.requested_by_name}`}
                  className="text-red-500 hover:text-red-600"><X size={17} /></Button>
              </>
            ) : <span className="text-xs text-slate-300">—</span>),
          },
        ]}
      />
      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="solicitações" />
    </div>
  );
}
